import * as React from 'react';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import { Link, useNavigate } from 'react-router-dom';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useState } from 'react';
import type { GenericList, ItemOptionList } from './models/JobSharedInterfaces';
import type { InvoiceItems } from './models/InvoiceInterfaces';

interface JobSearchResults {
    id: number;
    customer: string;
    siteAddress: string;
    finalQuoteNumber: string;
    status: string;
}

const InvoiceAddData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [jobIdSearch, setJobIdSearch] = useState(0);
    const [siteAddressSearch, setSiteAddressSearch] = useState("");
    const [invoiceTypes, setInvoiceTypes] = useState<GenericList[]>([]);
    const [itemTypes, setItemTypes] = useState<GenericList[]>([]);
    const [itemList, setItemList] = useState<ItemOptionList[]>([]);
    const [gst, setGst] = useState(0.01);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState<JobSearchResults[]>([]);
    const [jobId, setJobId] = useState(0);
    const [customerName, setCustomerName] = useState("");
    const [siteAddress, setSiteAddress] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [invoiceTypeId, setInvoiceTypeId] = useState(4);
    const [addNewRow, setAddNewRow] = useState(false);
    const [updateTotal, setUpdateTotal] = useState(false);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = useState("$0.00");
    
    const initItem: InvoiceItems = {
        itemIndex: 0,
        id: 0,
        jobInvoiceId: 0,
        branchIdent: "",
        item: "",
        itemId: -1,
        itemTypeId: -1,
        invItemType: "",
        invItemTypeId: -1,
        quantityType: "",
        purchaseOrderNumber: "",
        additionalHireRate: "0.00",
        rate: "0.00",
        rateFormatted: "$0.00",
        gst: "0.00",
        gstFormatted: "$0.00",
        chargeGst: true,
        amount: "0.00",
        amountFormatted: "$0.00",
        description: "",
        status: 1,
        invoiceStartDate: "",
        invoiceEndDate: "",
        invoiceStartDateFormatted: "",
        invoiceEndDateFormatted: "",
        hirePeriodDays: "",
        hirePeriod: "",
        dayRate: "",
        weekMonthRate: "",
        isDirty: false
    };
    const [items, setItems] = useState<InvoiceItems[]>([initItem]); 

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateInvoice");
        if (hasPermission) {
            getInvoiceData();
        } else {
            navigate("/accessdenied");
        }         
    }

    const getInvoiceData = async () => {
        const apiUrl = "GetNewInvoiceDropdowns";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setInvoiceTypes(result.data.invoiceTypes);
            setItemTypes(result.data.itemTypes);
            setItemList(result.data.items);
            setGst(result.data.gst);
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (updateTotal) {
            setUpdateTotal(false);
            calculateTotal();
        }
    }, [updateTotal]);

    React.useEffect(() => {
        if (addNewRow) {
            setAddNewRow(false);
            var newItem = addBlankLine(items.length);
            setItems([...items, newItem]);
        }
    }, [addNewRow])

    const search = (e: any) => {
        e.preventDefault();

        //check to make sure at least one search field has been entered
        if (jobIdSearch || siteAddressSearch) {
            searchJobs();
        } else {
            setShowResults(false);
            toast.error("Please enter at least one search item");
        }

    }

    const searchJobs = async () => {
        var jobIdSearchString = jobIdSearch > 0 ? jobIdSearch.toString() : "";
        const apiUrl = "SearchJobs?JobId=" + jobIdSearchString + '&CustomerId=&EventName=&QuoteNo=&SiteAddress=' + encodeURIComponent(siteAddressSearch) + '&Suburb=&PurchaseOrderNumber=&StatusId=-1&ShowCancelledJobs=false';
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setShowResults(true);
            setResults(result.data);
        }
    }

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const selectJob = (jobId: number, customer: string, siteAddress: string) => {
        clearError("jobId");

        //load new invoice based on job selected
        setJobId(jobId);
        setCustomerName(customer);
        setSiteAddress(siteAddress);
        setJobIdSearch(0);
        setSiteAddressSearch("");
        setShowResults(false);
        setResults([]);
    }

    const handleInvDateChange = (e: any) => {
        clearError(e.target.name);
        setInvoiceDate(e.target.value);
    }

    const handleDueDateChange = (e: any) => {
        clearError(e.target.name);
        setDueDate(e.target.value);
    }

    const handleInvTypeIdChange = (e: any) => {
        clearError(e.target.name);
        setInvoiceTypeId(Number(e.target.value));
    }

    const onCellChange = (itemIndex: number, e: any) => {
        e.preventDefault();

        clearError("invoiceItemsCheck");
        clearError("invoiceItems");

        setItems(prevState => prevState.map((item, index) =>
            index == itemIndex ? { ...item, [e.target.name]: e.target.value } : item
        ));

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        var lastrow = items.length - 1 == itemIndex;
        if (lastrow) {
            setAddNewRow(true);
            //var newItem = addBlankLine(items.length);
            //setItems([...items, newItem]);
        }
    }

    const onCellChangeNumber = (itemIndex: number, e: any, recalcTotal: boolean) => {
        e.preventDefault();

        clearError("invoiceItemsCheck");
        clearError("invoiceItems");

        var value = Number(e.target.value);

        setItems(prevState =>
            prevState.map((item, index) =>
                index == itemIndex ?
                {
                    ...item,
                    [e.target.name]: value
                } : item
            )
        );

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        var lastrow = items.length - 1 == itemIndex;
        if (lastrow) {
            setAddNewRow(true);
        //    var newItem = addBlankLine(items.length);
        //    setItems([...items, newItem]);
        }

        //recalculate the total invoice amount
        if (recalcTotal) {
            setUpdateTotal(true);
        }
    }

    const calculateTotal = () => {
        //recalc total
        var invTotal = 0;
        for (var i = 0; i < items.length - 1; i++) {
            invTotal += parseFloat(items[i].amount);
        }
        var invTotalFormatted = (invTotal).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setInvoiceTotalFormatted(invTotalFormatted);
    }

   
    const addBlankLine = (newItemIndex: number) => {
        var newItem: InvoiceItems ={
            itemIndex: newItemIndex,
            id: 0,
            jobInvoiceId: 0,
            branchIdent: "",
            item: "",
            itemId: -1,
            itemTypeId: -1,
            invItemType: "",
            invItemTypeId: -1,
            quantityType: "",
            purchaseOrderNumber: "",
            additionalHireRate: "0.00",
            rate: "0.00",
            rateFormatted: "$0.00",
            gst: "0.00",
            gstFormatted: "$0.00",
            chargeGst: true,
            amount: "0.00",
            amountFormatted: "$0.00",
            description: "",
            status: 1,
            invoiceStartDate: "",
            invoiceEndDate: "",
            invoiceStartDateFormatted: "",
            invoiceEndDateFormatted: "",
            hirePeriodDays: "",
            hirePeriod: "",
            dayRate: "",
            weekMonthRate: "",
            isDirty: false
        };
        return newItem;
    }

    const deleteInvoiceItem = (e: any, indexToRemove: number) => {
        e.preventDefault();

        setItems(items.filter((_, index) => index !== indexToRemove));
    }

    const validate = () => {
        //make sure all screens have valid data before saving
        let errorList: any = {};
        let formIsValid = true;
        var validDate = true;

        if (jobId <= 0) {
            formIsValid = false;
            errorList["jobId"] = "You must select a Job!";
        }

        if (invoiceDate == "") {
            validDate = false;
            formIsValid = false;
            errorList["invoiceDate"] = "Invoice Date is required";
        } else if (!DateHelpers.isValidDate(invoiceDate)) {
            validDate = false;
            formIsValid = false;
            errorList["invoiceDate"] = "Valid Invoice Date is required";
        } else if (DateHelpers.daysBetweenDates(DateHelpers.getCurrentDate(), invoiceDate) < -14) {
            validDate = false;
            formIsValid = false;
            errorList["invoiceDate"] = "Invoice Date must be within 14 days";
        }

        if (dueDate == "") {
            validDate = false;
            formIsValid = false;
            errorList["dueDate"] = "Due Date is required";
        } else if (!DateHelpers.isValidDate(dueDate)) {
            validDate = false;
            formIsValid = false;
            errorList["dueDate"] = "Valid Due Date is required";
        }

        if (validDate && !DateHelpers.compareDate(invoiceDate, dueDate)) {
            formIsValid = false;
            errorList["dueDate"] = "Due Date must be >= Invoice Date";
        }

        if (invoiceTypeId < 0) {
            formIsValid = false;
            errorList["invoiceTypeId"] = "Invoice Type is required";
        }

        //validate items
        if (items.length <= 1) {
            formIsValid = false;
            errorList["invoiceItemsCheck"] = "At least one item is required";
        } else {
            //check to make sure each item entered is valid
            var rowNumber = 0;

            var errorString = "Please enter the following values: ";
            var error = false;

            for (var i = 0; i < (items.length - 1); i++) {
                var validHireDate = true;
                rowNumber += 1;
                var newErrorString = "";

                if (items[i].itemTypeId < 1) {
                    newErrorString += "Type, ";
                    error = true;
                }
                if (items[i].itemId < 1) {
                    newErrorString += "Item, ";
                    error = true;
                }
                if (!items[i].description) {
                    newErrorString += "Description, ";
                    error = true;
                }
                if (!items[i].invoiceStartDate) {
                    validHireDate = false;
                    newErrorString += "Hire Start, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(items[i].invoiceStartDate)) {
                    validHireDate = false;
                    newErrorString += " Valid Hire Start, ";
                    error = true;
                }
                if (!items[i].invoiceEndDate) {
                    validHireDate = false;
                    newErrorString += "Hire End, ";
                    error = true;
                } else if (!DateHelpers.isValidDate(items[i].invoiceEndDate)) {
                    validHireDate = false;
                    newErrorString += " Valid Hire End, ";
                    error = true;
                }
                if (validHireDate && !DateHelpers.compareDate(items[i].invoiceStartDate, items[i].invoiceEndDate)) {
                    newErrorString += " Hire End must be >= Hire Start, ";
                    error = true;
                }
                if (items[i].amount.length < 0.1) {
                    newErrorString += "Amount, ";
                    error = true;
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
                
            }
            if (error) {
                formIsValid = false;
                errorList["invoiceItems"] = errorString;
            }

        }
        
        setErrors(errorList);
        return formIsValid;
    }


    const save = (e: any) => {
        e.preventDefault();
        if (validate()) {
            saveInvoice();
        }
    }

    const saveInvoice = async () => {
        //remove blank line
        var itemsToSave = [...items];
        itemsToSave.splice(items.length - 1, 1);

        var newInvoice = {
            jobId: jobId,
            invoiceDate: invoiceDate,
            dueDate: dueDate,
            invoiceTypeId: invoiceTypeId,
            items: itemsToSave
        };

        var jsonItem = JSON.stringify(newInvoice);

        const apiUrl = "SaveNewInvoice";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Invoice Saved");
                navigate("/invoices");
            }
        };
    }
   
    const renderDetails = (
        <div>
            <div className="makeitflex"> 
                <label className="input-group" htmlFor="jobIdSearch">
                    <span className="label">Job Id</span>
                    <input className="input" type="text" id="jobIdSearch" name="jobIdSearch" value={jobIdSearch} onChange={(e) => setJobIdSearch(Number(e.target.value))}></input>
                </label>
                <label className="input-group marginLeft10 marginRight10" htmlFor="siteAddressSearch">
                    <span className="label label--setsizesm">Site Address</span>
                    <input className="input" type="text" id="siteAddressSearch" name="siteAddressSearch" value={siteAddressSearch} onChange={(e) => setSiteAddressSearch(e.target.value)}></input>
                </label>
                <button className="defaultbutton marginBottom10" type='button' onClick={search}>Search</button>
            </div>
            <div className={showResults ? "" : "hidden"}>
                <div className={results.length > 0 ? 'overflowAuto' : 'hidden'}>
                    <table className='table tableColours'>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Job Id</th>
                                <th>Site Address</th>
                                <th>Customer</th>
                                <th>Quote Number</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map(job =>
                                <tr key={job.id}>
                                    <td><input type='checkbox' className="input" checked={false} onChange={() => selectJob(job.id, job.customer, job.siteAddress)} /></td>
                                    <td>{job.id}</td>
                                    <td>{job.siteAddress}</td>
                                    <td>{job.customer}</td>
                                    <td>{job.finalQuoteNumber}</td>
                                    <td>{job.status}</td>
                                    <td>
                                        <Link to={'/job/' + job.id} target="_blank" >
                                            Open
                                        </Link>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <span className={results.length == 0 ? "" : "hidden"}>There were no matching jobs found. Try modifying your search criteria.</span>
            </div>
            <div className="row">
                <hr></hr>
            </div>
            <div>
                <label className="input-group" htmlFor="jobId">
                    <span className="label">Job Id</span>
                    {jobId}
                </label>
                <span className="label errors">{errors["jobId"]}</span>
            </div>
            <div>
                <label className="input-group" htmlFor="customerName">
                    <span className="label">Customer</span>
                    {customerName}
                </label>
            </div>
            <div>
                <label className="input-group" htmlFor="siteAddress">
                    <span className="label">Site Address</span>
                    {siteAddress}
                </label>
            </div>
            <div>
                <label className="input-group" htmlFor="invoiceDate">
                    <span className="label">Invoice Date</span>
                    <input type='date' id="invoiceDate" name="invoiceDate" className="input" value={invoiceDate} onChange={(e) => handleInvDateChange(e)} />
                </label>
                <span className="label errors">{errors["invoiceDate"]}</span>
            </div>
            <div>
                <label className="input-group" htmlFor="dueDate">
                    <span className="label">Due Date</span>
                    <input type='date' id="dueDate" name="dueDate" className="input" value={dueDate} onChange={(e) => handleDueDateChange(e)} />
                </label>
                <span className="label errors">{errors["dueDate"]}</span>
            </div>
            <div>
                <label className="input-group" htmlFor="invoiceTypeId">
                    <span className="label">Invoice Type</span>
                    <select className="select" id="invoiceTypeId" name="invoiceTypeId" value={invoiceTypeId} onChange={(e) => handleInvTypeIdChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {invoiceTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <span className="label errors">{errors["invoiceTypeId"]}</span>
            </div>
            <div className="table__margin overflowAuto">
                <table className='table tableColours'>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Item</th>
                            <th>P/O # </th>
                            <th>Description</th>
                            <th>Hire Start</th>
                            <th>Hire End</th>
                            <th>Amount</th>
                            <th></th>
                        </tr>
                    </thead>
                    {items.map((item, index) =>
                        <tbody key={index}>
                            <tr>
                                <td className="table__text--align">
                                    <select className="select table__select--size" id="itemTypeId" name="itemTypeId" value={item.itemTypeId} onChange={(e) => onCellChangeNumber(index, e, false)} >
                                        <option hidden defaultValue="-1"></option>
                                        {itemTypes.map(type =>
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className="table__text--align">
                                    <select className="select table__select--size" id="itemId" name="itemId" value={item.itemId} onChange={(e) => onCellChangeNumber(index, e, false)} >
                                        <option hidden defaultValue="-1"></option>
                                        {itemList.map(type =>
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className="table__text--align">
                                    <input type="text" className="form-control invoicing__poSize" id="purchaseOrderNumber" name="purchaseOrderNumber" value={item.purchaseOrderNumber} onChange={(e) => onCellChange(index, e)} />
                                </td>
                                <td className="table__text--align">
                                    <input type="text" className="form-control" id="description" name="description" value={item.description} onChange={(e) => onCellChange(index, e)} />
                                </td>
                                <td className="table__text--align">
                                    <input type="date" className="form-control" id="invoiceStartDate" name="invoiceStartDate" value={item.invoiceStartDate} onChange={(e) => onCellChange(index, e)} />
                                </td>
                                <td className="table__text--align">
                                    <input type="date" className="form-control" id="invoiceEndDate" name="invoiceEndDate" value={item.invoiceEndDate} onChange={(e) => onCellChange(index, e)} />
                                </td>
                                <td className="table__text--align table__input--alignright">
                                    <input type='number' min="0" step="0.01" className="form-control table__input--alignright table__input--min100" id="amount" name="amount" value={item.amount} onChange={(e) => onCellChangeNumber(index, e, true)} />
                                </td>
                                <td className="table__text--align">
                                    {(index != (items.length - 1)) ?
                                        <div className="delete--tablecell">
                                            <a className="makeitred" href="#" onClick={(e) => deleteInvoiceItem(e, index)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div> : ""
                                    }
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
                <span className="label errors">{errors["invoiceItemsCheck"]}</span>
                {errors["invoiceItems"] ?
                    (errors["invoiceItems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
            </div>
            <div className="alignitemsright-centretext job__quote--total">
                Invoice Total {invoiceTotalFormatted}
            </div>
            <div>
                <button className="defaultbutton" type="button" onClick={save}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return <div>
        <h1>New Invoice</h1>
        {contents}
    </div>;

}

export default InvoiceAddData;